import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { reviewService } from '../../../services/reviewService';

const ModerationTab = () => {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingReviewId, setProcessingReviewId] = useState(null);

  useEffect(() => {
    fetchPendingReviews();
  }, []);

  const fetchPendingReviews = async () => {
    try {
      setIsLoading(true);
      setError('');
      const reviews = await reviewService?.getPendingReviews();
      setPendingReviews(reviews);
    } catch (err) {
      console.error('Error fetching pending reviews:', err);
      setError(err?.message || 'Yorumlar yüklenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReviewAction = async (reviewId, status, reviewType) => {
    try {
      setProcessingReviewId(reviewId);
      setError('');

      await reviewService?.updateReviewStatus(reviewId, status, reviewType);

      // Remove the review from the list immediately for smooth UX
      setPendingReviews((prev) => prev?.filter((review) => review?.id !== reviewId));

      // Show success feedback could be added here
    } catch (err) {
      console.error('Error updating review status:', err);
      setError(err?.message || 'Yorum durumu güncellenirken bir hata oluştu');
    } finally {
      setProcessingReviewId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-card rounded-xl border border-border p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Bekleyen yorumlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0" />
          <div className="flex-1">
            <p className="text-destructive font-medium mb-2">Hata</p>
            <p className="text-sm text-destructive">{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={fetchPendingReviews}
            >
              Tekrar Dene
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-1">
            Yorum Moderasyonu
          </h2>
          <p className="text-sm text-muted-foreground">
            Bekleyen yorumları onayla veya reddet
          </p>
        </div>
        <Button
          variant="outline"
          iconName="RefreshCw"
          onClick={fetchPendingReviews}
          disabled={isLoading}
        >
          Yenile
        </Button>
      </div>
      {pendingReviews?.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} className="text-success" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Tüm Yorumlar İşlendi
          </h3>
          <p className="text-muted-foreground">
            Şu anda onay bekleyen yorum bulunmuyor.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {pendingReviews?.map((review) => (
            <div
              key={review?.id}
              className="bg-card rounded-xl border border-border p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start gap-4">
                {/* User Avatar */}
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                  {review?.user?.avatarUrl ? (
                    <img
                      src={review?.user?.avatarUrl}
                      alt={review?.user?.fullName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={24} className="text-muted-foreground" />
                  )}
                </div>

                {/* Review Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <p className="font-semibold text-foreground">
                        {review?.user?.fullName || 'Anonim Kullanıcı'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {review?.type === 'spot_review' && review?.spot?.name}
                        {review?.type === 'event_review' && review?.event?.title}
                        {review?.type === 'blog_comment' && review?.post?.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {review?.type === 'spot_review' && 'Spot Yorumu'}
                        {review?.type === 'event_review' && 'Etkinlik Yorumu'}
                        {review?.type === 'blog_comment' && 'Blog Yorumu'}
                      </p>
                    </div>
                    {review?.rating && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {[...Array(5)]?.map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={16}
                            className={
                              i < review?.rating
                                ? 'text-yellow-500 fill-yellow-500' :'text-muted-foreground'
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  <p className="text-foreground mb-3">{review?.comment}</p>

                  {/* Review Images */}
                  {review?.imageUrls && review?.imageUrls?.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {review?.imageUrls?.map((url, index) => (
                        <img
                          key={index}
                          src={url}
                          alt={`Review image ${index + 1}`}
                          className="w-full h-20 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      <span>
                        {new Date(review.createdAt)?.toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      iconName="CheckCircle"
                      onClick={() => handleReviewAction(review?.id, 'approved', review?.type)}
                      disabled={processingReviewId === review?.id}
                      className="bg-success hover:bg-success/90 text-white"
                    >
                      {processingReviewId === review?.id ? 'İşleniyor...' : 'Onayla'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="XCircle"
                      onClick={() => handleReviewAction(review?.id, 'rejected', review?.type)}
                      disabled={processingReviewId === review?.id}
                      className="border-destructive text-destructive hover:bg-destructive/10"
                    >
                      {processingReviewId === review?.id ? 'İşleniyor...' : 'Reddet'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModerationTab;